import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '../../environments/environment';
import { forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { Preview, PreviewApiResponse, PreviewsApiResponse } from '@models/interfaces/core/Preview.interface';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { APP_CONFIG } from '@configuration/configuration';
import { ConfigurationService } from './configuration.service';
import { APP_PREVIEW } from '@configuration/preview';
import { TemplateService } from './template.service';
import { TemplateData } from '@models/interfaces/dvm/Template.interface';
import { Pagination } from '@models/interfaces/components/Pagination.interface';
import type { ApiResponse } from '../models/interfaces/core/ApiResponse.interface';
import { blobUrlToFile } from '@helpers/utils/blob-url-to-file.util';

export interface PublishData {
  url: string;
  slug: string;
  active: boolean;
  test: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PreviewService {

  // SERVICES
  private readonly http:          HttpClient                    = inject(HttpClient);
  private readonly config:        WritableSignal<Configuration> = inject(APP_CONFIG);
  private readonly configService: ConfigurationService          = inject(ConfigurationService);
  private readonly template:      TemplateService               = inject(TemplateService);

  // STATE (?)
  public isLoadingPreview: WritableSignal<boolean>                   = signal(false);
  private preview:         WritableSignal<Preview | null>            = inject(APP_PREVIEW);  
  public  previews:        WritableSignal<Preview[]>                 = signal([]);
  public  pagination:      WritableSignal<Pagination>                = signal({} as Pagination);
  private previewCache:    Record<number, Preview[]>                 = {};

  public setPreview(preview: Preview): void {
    this.preview.set(preview);
  }

  public resetPreview(): void {
    this.preview.set(null);
  }

  // PUBLIC ENDPOINTS
  public getPublicPreview(slug: string): Observable<Preview> {
    const endpoint: string = `${environment.apiUrl}/api/previews/${slug}`;
    return this.http
      .get<PreviewApiResponse>(endpoint)
      .pipe(map(res => res.data));
  }

  public getMasterConfig(url: string): Observable<Configuration> {
    return this.http.get<Configuration>(url, { headers: {
      'cache-control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
    }});
  }

  public getRedirectUrl(slug: string): Observable<{ redirectUrl: string | null}> {
    const endpoint: string = `${environment.apiUrl}/api/previews/redirect/${slug}`;
    return this.http
      .get<{message: string, data: { redirectUrl: string | null}}>(endpoint)
      .pipe(map(res => res.data.redirectUrl ? { redirectUrl: res.data.redirectUrl } : { redirectUrl: null }));
  }

  public duplicatePreview(slug: string, newSlug: string): Observable<Preview> {
    const endpoint: string = `${environment.apiUrl}/api/previews/manage/${slug}/duplicate`;
    return this.http.post<ApiResponse<Preview>>(endpoint, { newSlug }).pipe(map(res => res.data));
  }

  // PRIVATE ENDPOINTS
  public getPrivatePreview(slug: string): Observable<Preview> {
    this.isLoadingPreview.set(true);
    const endpoint: string = `${environment.apiUrl}/api/previews/manage/slug/${slug}`;
    return this.http
      .get<PreviewApiResponse>(endpoint)
      .pipe(
        tap(() => setTimeout(() => this.isLoadingPreview.set(false), 1000)),
        map(res => res.data)
      );
  }

  /**
   * Método que rellena la signal de previews con las previews disponibles, aplicando paginación, búsqueda y cache.
   * Si hay busqueda, se fuerza o no hay cache, se hace petición al backend. Si no, se carga desde cache (si existe) o se hace petición (si no existe).
   * Si no se hace petición, se actualiza la paginación con la página actual.
   * @param page 
   * @param search 
   * @param force 
   * @returns 
   */
  public getPreviews(page: number, search?: string, force?: boolean): Observable<void> {

    if(search || force || !(page in this.previewCache)) 
    return this._getPreviews(page, search).pipe(map((res: PreviewsApiResponse): void => this.previews.set(res.data.data)));

    
    
    if(this.previewCache[page]) {
      
      this.pagination.update((pagination: Pagination) => ({ ...pagination, current_page: page }));

      this.previews.set(this.previewCache[page]);

    } 

    return of(undefined);

  }

  public deletePreview(preview: Preview): Observable<Preview> {
    const endpoint: string = `${environment.apiUrl}/api/previews/manage/${preview.id}`;
    return this.http.delete<Preview>(endpoint);
  }

  public createPreview(publishData: PublishData): Observable<Preview> {
    const template = this.template.selectedTemplate();
    const templateId = template?.isGlobal ? template.id?.toString() : undefined;
    const formData = this.buildPublishFormData(publishData, templateId);

    return this.appendBrandingFiles(formData).pipe(
      switchMap((body) => this.http.post<Preview>(
        `${environment.apiUrl}/api/previews/manage/publish`,
        body
      ))
    );
  }

  public updatePreview(publishData: PublishData, preview: Preview): Observable<Preview> {

    const template: TemplateData | null = this.template.selectedTemplate();
    const templateId = template?.isGlobal && template.id ? template.id.toString() : '';
    const formData = this.buildPublishFormData(publishData, templateId);

    return this.appendBrandingFiles(formData).pipe(
      switchMap((body) => this.http.patch<Preview>(
        `${environment.apiUrl}/api/previews/manage/${preview.id}`,
        body
      ))
    );
  }

  private buildPublishFormData(
    publishData: PublishData,
    globalTemplateId?: string
  ): FormData {
    const formData = new FormData();

    formData.append('slug', publishData.slug);
    formData.append('url', environment.previewUrl + publishData.slug);
    formData.append('active', String(publishData.active));
    formData.append('test', String(publishData.test));
    formData.append('mapId', this.config().Dvm.viewer.mapId);
    formData.append('venueCode', this.config().Dvm.viewer.venueId);
    formData.append('clientKey', 'C-95a684ae-9a57-466e-9028-ce953194b7f5');
    formData.append('venueKey', 'V-XXXXX');
    formData.append('masterConfig', this.configService.getMasterConfigBlob(), 'masterConfig.json');

    if (globalTemplateId !== undefined) {
      formData.append('globalTemplateId', globalTemplateId);
    }

    return formData;
  }

  private appendBrandingFiles(formData: FormData): Observable<FormData> {
    return forkJoin({
      logo: blobUrlToFile(this.config().General.client.logo, 'logo.png'),
      favicon: blobUrlToFile(this.config().General.client.favicon, 'favicon.png'),
    }).pipe(
      map(({ logo, favicon }) => {
        formData.append('logo', logo, 'logo.png');
        formData.append('favicon', favicon, 'favicon.png');
        return formData;
      })
    );
  }

  public updateRedirectUrl(id: number, redirectUrl: string | null): Observable<Preview> {
    const endpoint: string = `${environment.apiUrl}/api/previews/redirect/${id}`;
    const body: { redirectUrl: string | null } = { redirectUrl: redirectUrl || null };
    return this.http
      .patch<PreviewApiResponse>(endpoint, body)
      .pipe(map(res => res.data));
  }

  private _getPreviews(page: number, search?: string): Observable<PreviewsApiResponse> {

    const endpoint:    string     = `${environment.apiUrl}/api/previews/manage`;
    let   queryParams: HttpParams = new HttpParams();

    // Paginacion
    queryParams = queryParams.append('page', page.toString());

    // Search
    if(search) queryParams = queryParams.append('search', encodeURIComponent(search));

    return this.http.get<PreviewsApiResponse>(endpoint, { params: queryParams }).pipe(
      tap((res: PreviewsApiResponse): void => {
        
        // Si hay busqueda no se guarda en cache, de hecho se borra la caché de esa pagina 
        // para forzar que si se hace otra busqueda o se reestablece el filtro
        // se haga la petición al backend. Normalmente se busca estando en pagina 1 
        // y esa pagina se llena en la llamada inicial del componente List, asi que evitamos conflictos.

        if(!search) this.previewCache[page] = res.data.data
        else delete this.previewCache[page];
        
        // Si se hace llamada siempre se pisa la paginación
        this.pagination.set(res.data.meta);
        
      })
    );

  }
}
